/**
 * Utility for robust transporter identity matching.
 * Handles variations in IDs, Names, Short Names, and Supplier Codes.
 */

/**
 * Checks if a reservation record matches a set of transporter identities.
 * @param {Object} reservation - The reservation record from Firestore.
 * @param {Set|Array} identities - A set/array of known identity strings (lowercase).
 * @returns {Boolean} - True if matches.
 */
export const isTransporterMatch = (reservation, identities) => {
    if (!reservation || !identities) return false;

    const identitySet = identities instanceof Set ? identities : new Set(identities.map(i => i.toLowerCase().trim()).filter(Boolean));
    if (identitySet.size === 0) return false;

    const rId = (reservation.transporterId || '').toString().trim().toLowerCase();
    const rNames = [
        (reservation.transporterName || '').toLowerCase().trim(),
        (reservation.transporterShortName || '').toLowerCase().trim(),
        (reservation.transporter || '').toLowerCase().trim(),
        (reservation.companyName || '').toLowerCase().trim(),
        (reservation.supplierCode || reservation.metrics?.supplierCode || '').toLowerCase().trim(),
        (reservation.metrics?.transporterName || '').toLowerCase().trim()
    ].filter(n => n && n.length >= 2);

    // 1. Direct ID Match
    if (rId && identitySet.has(rId)) return true;

    // 2. Name Match (Loose/Inclusive)
    const hasNameMatch = rNames.some(rn => {
        return Array.from(identitySet).some(identity => {
            if (!identity || identity.length < 3) return rn === identity; // Exact match for tiny strings
            return rn.includes(identity) || identity.includes(rn);
        });
    });

    if (hasNameMatch) return true;

    return false;
};

/**
 * Builds a set of identities for a transporter based on its master data.
 * @param {Object} transporter - Transporter master record.
 * @returns {Set} - Set of identity strings.
 */
export const buildTransporterIdentities = (transporter) => {
    const identities = new Set();
    if (!transporter) return identities;

    if (transporter.id) identities.add(transporter.id.toLowerCase().trim());
    if (transporter.companyName) identities.add(transporter.companyName.toLowerCase().trim());
    if (transporter.shortName) identities.add(transporter.shortName.toLowerCase().trim());
    if (transporter.supplierCode) identities.add(transporter.supplierCode.toLowerCase().trim());

    return identities;
};

/**
 * Builds identities based on user profile (for logged in transporters).
 */
export const buildUserIdentities = (userData) => {
    const identities = new Set();
    if (!userData) return identities;

    const searchId = (userData.transporterId || userData.scope?.transporterId || '').toString().trim().toLowerCase();
    const uTransporterName = (userData.transporterName || '').toLowerCase().trim();
    const uCompanyName = (userData.companyName || '').toLowerCase().trim();
    const uShortName = (userData.transporterShortName || '').toLowerCase().trim();
    const uSupplierCode = (userData.supplierCode || userData.scope?.supplierCode || '').toLowerCase().trim();

    if (searchId) identities.add(searchId);
    if (uTransporterName) identities.add(uTransporterName);
    if (uCompanyName) identities.add(uCompanyName);
    if (uShortName) identities.add(uShortName);
    if (uSupplierCode) identities.add(uSupplierCode);

    return identities;
};
